import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, TrendingDown, TrendingUp, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, AreaChart, Area, Cell,
} from "recharts";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { fmtNum } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

/* ── Brand colors ── */
const BURGUNDY   = "#910029";
const CHARCOAL   = "#393F4B";
const ALICE      = "#ECF4F7";
const SLATE_GRID = "#4A5260";
const SLATE_AXIS = "#8B9BB8";
const TOOLTIP_BG = "#2E353F";
const TEAL       = "#10B981";
const AMBER      = "#F5B800";

const CHART_TOOLTIP = {
  contentStyle: {
    background: TOOLTIP_BG,
    border: `1px solid ${SLATE_GRID}`,
    borderRadius: 8,
    fontSize: 12,
    color: ALICE,
  },
  labelStyle: { color: ALICE },
  cursor: { fill: "rgba(255,255,255,0.04)" },
};

const TILES = [
  { key: "certified_revenue", label: "Certified Revenue",  color: TEAL    },
  { key: "actual_cost",       label: "Actual Cost",         color: BURGUNDY },
  { key: "margin",            label: "Gross Margin",        color: "#38BDF8" },
  { key: "cash_position",     label: "Cash Position",       color: "#A78BFA" },
  { key: "payables",          label: "Payables",            color: AMBER    },
  { key: "receivables",       label: "Receivables",         color: "#FB923C" },
];

export default function ExecutiveDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reports/executive-dashboard")
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="executive-dashboard-page">
      <PageHeader
        title="Executive Dashboard"
        description="Portfolio-wide revenue, cost, cash and risk in one view."
      />

      {loading || !data ? (
        <div className="flex h-64 items-center justify-center">
          <p className="text-sm text-muted-foreground">Loading…</p>
        </div>
      ) : (
        <>
          {/* ── KPI tiles ── */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="executive-tiles">
            {TILES.map((tile) => {
              const val = Number(data.tiles[tile.key] ?? 0);
              const isMargin = tile.key === "margin";
              return (
                <div
                  key={tile.key}
                  className="rounded-xl border border-border bg-card p-4"
                  data-testid={`tile-${tile.key}`}
                  style={{ borderLeftWidth: 3, borderLeftColor: tile.color }}
                >
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{tile.label}</p>
                  <p className="mt-2 font-mono text-xl font-bold text-foreground">
                    <span className="whitespace-nowrap">{val < 0 ? "−" : ""}₹{fmtNum(Math.abs(val), 0)}</span>
                  </p>
                  {isMargin && (
                    <div className="mt-1 flex items-center gap-1">
                      {val >= 0
                        ? <ArrowUpRight className="h-3.5 w-3.5 text-emerald-500" />
                        : <ArrowDownRight className="h-3.5 w-3.5 text-rose-500" />}
                      <span className={`text-[11px] font-medium ${val >= 0 ? "text-emerald-500" : "text-rose-500"}`}>
                        {val >= 0 ? "Profitable" : "Overspent"}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Spend vs Progress chart ── */}
          <div className="rounded-xl border border-border bg-card p-4" data-testid="spend-vs-progress-chart">
            <h3 className="mb-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Cost % vs Progress % — by Project
            </h3>
            <p className="mb-4 text-xs text-muted-foreground">
              Cost ahead of progress signals budget overrun risk.
            </p>
            {data.progress_vs_cost.length === 0 ? (
              <EmptyState title="No active projects" testId="spend-vs-progress-empty" />
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.progress_vs_cost} barCategoryGap="30%">
                  <CartesianGrid strokeDasharray="3 3" stroke={SLATE_GRID} vertical={false} />
                  <XAxis
                    dataKey="project_code"
                    stroke={SLATE_AXIS}
                    fontSize={11}
                    tick={{ fill: ALICE, opacity: 0.7 }}
                  />
                  <YAxis
                    stroke={SLATE_AXIS}
                    fontSize={11}
                    tick={{ fill: ALICE, opacity: 0.7 }}
                    tickFormatter={(v) => `${v}%`}
                    domain={[0, 100]}
                  />
                  <Tooltip {...CHART_TOOLTIP} formatter={(v, name) => [`${Number(v).toFixed(1)}%`, name]} />
                  <Legend
                    wrapperStyle={{ fontSize: 12, color: ALICE, opacity: 0.7 }}
                    iconType="square"
                  />
                  <Bar dataKey="progress_pct" name="Progress %" fill={TEAL} radius={[3, 3, 0, 0]} maxBarSize={32} />
                  <Bar dataKey="cost_pct" name="Cost %" fill={BURGUNDY} radius={[3, 3, 0, 0]} maxBarSize={32} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* ── Monthly Cash-Flow chart ── */}
          <div className="rounded-xl border border-border bg-card p-4" data-testid="monthly-cashflow-chart">
            <h3 className="mb-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Monthly Cash-Flow — Last 6 Months
            </h3>
            <p className="mb-4 text-xs text-muted-foreground">
              Income (inflow) vs expenses (outflow) from GL journal entries.
            </p>
            {(!data.monthly_cashflow || data.monthly_cashflow.length === 0) ? (
              <EmptyState title="No GL entries found" testId="cashflow-empty" />
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={data.monthly_cashflow}>
                  <defs>
                    <linearGradient id="inflow-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={TEAL} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={TEAL} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="outflow-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={BURGUNDY} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={BURGUNDY} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={SLATE_GRID} vertical={false} />
                  <XAxis
                    dataKey="month"
                    stroke={SLATE_AXIS}
                    fontSize={11}
                    tick={{ fill: ALICE, opacity: 0.7 }}
                  />
                  <YAxis
                    stroke={SLATE_AXIS}
                    fontSize={11}
                    tick={{ fill: ALICE, opacity: 0.7 }}
                    tickFormatter={(v) => `₹${fmtNum(v, 0)}`}
                  />
                  <Tooltip
                    {...CHART_TOOLTIP}
                    formatter={(v, name) => [`₹${fmtNum(v, 0)}`, name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, color: ALICE, opacity: 0.7 }} iconType="square" />
                  <Area
                    type="monotone"
                    dataKey="inflow"
                    name="Income"
                    stroke={TEAL}
                    strokeWidth={2}
                    fill="url(#inflow-grad)"
                  />
                  <Area
                    type="monotone"
                    dataKey="outflow"
                    name="Expenses"
                    stroke={BURGUNDY}
                    strokeWidth={2}
                    fill="url(#outflow-grad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* ── Bottom row: Top overdue + Issues by project ── */}
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Top overdue / risks */}
            <div className="rounded-xl border border-border bg-card" data-testid="top-overdue-list">
              <div className="border-b border-border px-4 py-3 flex items-center gap-2">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Top Overdue Items
                </h3>
              </div>
              {data.top_overdue.length === 0 ? (
                <EmptyState title="Nothing overdue" testId="top-overdue-empty" />
              ) : (
                <ul className="divide-y divide-border">
                  {data.top_overdue.map((item) => (
                    <li
                      key={item.id}
                      className="flex items-center justify-between gap-3 px-4 py-2.5"
                      data-testid={`overdue-item-${item.id}`}
                    >
                      <span className="flex items-center gap-2 text-sm text-foreground">
                        <AlertTriangle
                          className={`h-3.5 w-3.5 shrink-0 ${item.severity === "CRITICAL" ? "text-rose-400" : "text-amber-400"}`}
                        />
                        <span className="truncate">{item.title}</span>
                      </span>
                      <Badge
                        variant={item.severity === "CRITICAL" ? "destructive" : "secondary"}
                        className="shrink-0 font-mono text-[11px]"
                      >
                        {item.days_overdue}d
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Open issues — horizontal bar chart */}
            <div className="rounded-xl border border-border bg-card" data-testid="open-issues-chart">
              <div className="border-b border-border px-4 py-3">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Open Issues by Project
                </h3>
              </div>
              {data.open_issues_by_project.length === 0 ? (
                <EmptyState title="No open issues" testId="open-issues-empty" />
              ) : (
                <div className="p-4">
                  <ResponsiveContainer width="100%" height={Math.max(160, data.open_issues_by_project.length * 36)}>
                    <BarChart
                      data={data.open_issues_by_project}
                      layout="vertical"
                      margin={{ left: 8, right: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={SLATE_GRID} horizontal={false} />
                      <XAxis
                        type="number"
                        stroke={SLATE_AXIS}
                        fontSize={11}
                        tick={{ fill: ALICE, opacity: 0.7 }}
                        allowDecimals={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="project_code"
                        stroke={SLATE_AXIS}
                        fontSize={11}
                        tick={{ fill: ALICE, opacity: 0.7 }}
                        width={60}
                      />
                      <Tooltip {...CHART_TOOLTIP} formatter={(v) => [v, "Issues"]} />
                      <Bar dataKey="count" name="Issues" radius={[0, 3, 3, 0]} maxBarSize={20}>
                        {data.open_issues_by_project.map((entry, i) => (
                          <Cell
                            key={i}
                            fill={entry.count >= 5 ? BURGUNDY : entry.count >= 2 ? AMBER : TEAL}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* ── Progress vs Cost table ── */}
          <div className="rounded-xl border border-border bg-card" data-testid="progress-vs-cost-table">
            <div className="border-b border-border px-4 py-3">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Progress vs Cost — Detail
              </h3>
            </div>
            {data.progress_vs_cost.length === 0 ? (
              <EmptyState title="No active projects" testId="progress-vs-cost-empty" />
            ) : (
              <div className="divide-y divide-border">
                {data.progress_vs_cost.map((row) => {
                  // Same rule as the Home health card (services/portfolio.py): spend 10+ points ahead of progress is "Cost Ahead".
                  const gap = Number(row.cost_pct) - Number(row.progress_pct);
                  const ahead = gap >= 10;
                  const onPar = !ahead && gap > -2;
                  return (
                    <div
                      key={row.project_id}
                      className="flex items-center justify-between px-4 py-3"
                      data-testid={`progress-vs-cost-row-${row.project_code}`}
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {row.project_code} — {row.project_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Progress {fmtNum(row.progress_pct, 1)}% &middot; Cost {fmtNum(row.cost_pct, 1)}%
                        </p>
                      </div>
                      {onPar ? (
                        <Badge variant="secondary" className="gap-1">
                          <Minus className="h-3 w-3" /> On Par
                        </Badge>
                      ) : ahead ? (
                        <Badge variant="destructive" className="gap-1">
                          <TrendingUp className="h-3 w-3" /> Cost Ahead
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="gap-1 text-emerald-500">
                          <TrendingDown className="h-3 w-3" /> On Track
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
